'use client';

import dynamic from 'next/dynamic';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import logTrailDetailsConditionsViewed from '@alltrails/amplitude/events/logTrailDetailsConditionsViewed';
import logTrailDetailsDateSelected from '@alltrails/amplitude/events/logTrailDetailsDateSelected';
import { useGetTrailWeatherConditionsQuery, useGetTrailWeatherOverviewQuery } from '@alltrails/conditions/redux/conditionsApi';
import { updateIsConditionsPreviewActive, updateSelectedUTC } from '@alltrails/conditions/redux/conditionsSlice';
import { getLocalizedDateStart, getLocalizedDateString, getLocalizedUTCDate, getUTCFromLocalized } from '@alltrails/conditions/utils/timeHelpers';
import useUser from '@alltrails/context/hooks/useUser';
import useIsVisible from '@alltrails/core/hooks/useIsVisible';
import { millisecondsInDay } from '@alltrails/data-formatting/dateConstants';
import Heading from '@alltrails/denali/components/Heading';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import { useExposureEvent } from '@alltrails/experiments';
import type { User } from '@alltrails/types/User';
import {
  getTrailConditionsAlertDisplayed,
  getTrailDetailsDaysInAdvance,
  getTrailDetailsPageTerrainCondition
} from '@/utils/conditionsAmplitudeHelpers';
import { useAppDispatch, useAppSelector } from '@/utils/redux';
import TrailConditionsCard from '../TrailConditionsCard/TrailConditionsCard';
import TrailConditionsErrorCard from '../TrailConditionsErrorCard/TrailConditionsErrorCard';
import TrailConditionsUpsellCard, { TrailConditionsUpsellCardLoading } from '../TrailConditionsUpsellCard/TrailConditionsUpsellCard';
import { trailConditionsIntervalMs } from '../TrailMapConditionsSidebar/constants';
import { Day, SelectedDay } from './dayTypes';
import DesktopDailySummaries, { DesktopDailySummariesLoading } from './DesktopDailySummaries';
import MobileDailySummaries, { MobileDailySummariesLoading } from './MobileDailySummaries';
import styles from './TrailConditions.module.scss';

const ConditionsUpsellModal = dynamic(() => import('@alltrails/conditions/components/ConditionsUpsellModal'), { ssr: false });

type TrailConditionsProps = {
  trailId: number;
};

const TrailConditions = ({ trailId }: TrailConditionsProps) => {
  const isMobile = useIsMobileSizedScreen();
  const dispatch = useAppDispatch();
  const user = useUser();
  const showConditionsCard = user?.featureEntitlements?.includes('advanced_conditions');
  const { displayMetric, selectedUTC } = useAppSelector(state => ({
    displayMetric: state.context.displayMetric,
    selectedUTC: state.conditions.selectedUTC
  }));

  const sendGroundConditionsExposure = useExposureEvent('web-disco-rm-ground-conditions');

  useEffect(() => {
    sendGroundConditionsExposure();
  }, [sendGroundConditionsExposure]);

  useEffect(() => {
    const initialUTC = Math.floor(Date.now() / trailConditionsIntervalMs) * trailConditionsIntervalMs;
    dispatch(updateSelectedUTC(initialUTC));
  }, [dispatch]);

  const [utcOffsetSeconds, setUTCOffsetSeconds] = useState<number>();

  const localizedDateString = useMemo(
    () => (selectedUTC && utcOffsetSeconds !== undefined ? getLocalizedDateString(selectedUTC, utcOffsetSeconds) : undefined),
    [selectedUTC, utcOffsetSeconds]
  );

  const { data, isError, isFetching, isLoading, refetch } = useGetTrailWeatherOverviewQuery({
    date: localizedDateString,
    includeGradient: true,
    includePrecipitation: true,
    sentenceUnits: displayMetric ? 'm' : 'i',
    trailId
  });

  useEffect(() => {
    setUTCOffsetSeconds(data?.secondsFromUtc);
  }, [data?.secondsFromUtc]);

  const utcTodayStart = useMemo(
    () => (utcOffsetSeconds !== undefined ? getLocalizedDateStart(Date.now(), utcOffsetSeconds) : undefined),
    [utcOffsetSeconds]
  );

  const selectedDayStart = useMemo(
    () => (selectedUTC && utcOffsetSeconds !== undefined ? getLocalizedDateStart(selectedUTC, utcOffsetSeconds) : undefined),
    [selectedUTC, utcOffsetSeconds]
  );

  const { alert, overview } = useMemo(() => {
    if (!data || isFetching) {
      // When isFetching is true, a different day has been selected, but the alert/overview could still exist from a prior call, so ignore the data until isFetching is false
      return { alert: undefined, overview: undefined };
    }
    return data;
  }, [data, isFetching]);

  const { data: conditionsData } = useGetTrailWeatherConditionsQuery(
    { date: localizedDateString || '', sentenceUnits: displayMetric ? 'm' : 'i', trailId },
    { skip: !localizedDateString || !showConditionsCard }
  );

  const hasLoggedViewEvent = useRef(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const isVisible = useIsVisible(contentRef);

  useEffect(() => {
    if (isVisible && !hasLoggedViewEvent.current) {
      if (showConditionsCard) {
        if (conditionsData) {
          const { conditionKey, relatedReviewCount, subtitleSource } = conditionsData.terrain;
          logTrailDetailsConditionsViewed({
            alert_displayed: getTrailConditionsAlertDisplayed(alert?.alertType),
            has_alert: Boolean(alert),
            num_terrain_reviews_shown: relatedReviewCount || 0,
            terrain_subtitle_shown: subtitleSource === 'reviews' ? 'related reviews' : 'ugc',
            terrain_type_shown: getTrailDetailsPageTerrainCondition(conditionKey),
            trail_id: trailId
          });
          hasLoggedViewEvent.current = true;
        }
      } else {
        logTrailDetailsConditionsViewed({
          alert_displayed: getTrailConditionsAlertDisplayed(alert?.alertType),
          has_alert: Boolean(alert),
          num_terrain_reviews_shown: 0,
          trail_id: trailId
        });
        hasLoggedViewEvent.current = true;
      }
    }
  }, [alert, conditionsData, isVisible, showConditionsCard, trailId]);

  const days = useMemo(() => {
    if (data?.dailySummaries && utcTodayStart) {
      return data.dailySummaries.map<Day | SelectedDay>(({ gradient, highTemperature, iconCode, lowTemperature }, index) => {
        // The start of each day, localized to the trail's timezone
        const utcMs = utcTodayStart + millisecondsInDay * index;
        const isSelected = utcMs === selectedDayStart;

        if (isSelected && overview && utcOffsetSeconds !== undefined) {
          return {
            alert,
            gradient,
            precipitationChance: overview.chanceOfPrecipitation,
            summary: overview.summary,
            sunrise: overview.sunriseTime ? getLocalizedUTCDate(overview.sunriseTime, utcOffsetSeconds) : undefined,
            sunset: overview.sunsetTime ? getLocalizedUTCDate(overview.sunsetTime, utcOffsetSeconds) : undefined,
            tempCurrent: overview.currentTemperature,
            tempHigh: overview.highTemperature,
            tempLow: overview.lowTemperature,
            utcMs,
            weatherIconCode: iconCode // Use the icon code from the daily summary rather than the overview to avoid the icon changing when the selected day changes
          };
        }

        return { gradient, tempHigh: highTemperature, tempLow: lowTemperature, utcMs, weatherIconCode: iconCode };
      });
    }
    return undefined;
  }, [alert, data?.dailySummaries, overview, selectedDayStart, utcOffsetSeconds, utcTodayStart]);

  const onChange = useCallback(
    (localizedUTC: number, index: number) => {
      if (utcOffsetSeconds !== undefined) {
        const newSelectedUTC = getUTCFromLocalized(localizedUTC, utcOffsetSeconds);
        dispatch(updateSelectedUTC(newSelectedUTC));
        logTrailDetailsDateSelected({ days_in_advance: getTrailDetailsDaysInAdvance(index), trail_id: trailId });
      }
    },
    [dispatch, trailId, utcOffsetSeconds]
  );

  const renderDaySelectComponent = () => {
    if (isError) {
      return (
        <TrailConditionsErrorCard
          className={styles.overviewErrorCard}
          onRetry={refetch}
          showIllustration={isMobile ? false : true}
          trailId={trailId}
        />
      );
    }

    if (isMobile) {
      if (isLoading || !days || !selectedDayStart) {
        return <MobileDailySummariesLoading />;
      }
      return <MobileDailySummaries days={days} onChange={onChange} selectedUTCMs={selectedDayStart} />;
    }

    if (isLoading || !days || !selectedDayStart) {
      return <DesktopDailySummariesLoading />;
    }
    return <DesktopDailySummaries days={days} onChange={onChange} selectedUTCMs={selectedDayStart} />;
  };

  const [initialTheme, setInitialTheme] = useState(overview?.backgroundTheme);
  const [showConditionsUpsellModal, setShowConditionsUpsellModal] = useState(false);

  useEffect(() => {
    if (!initialTheme && overview?.backgroundTheme) {
      setInitialTheme(overview?.backgroundTheme);
    }
  }, [initialTheme, overview?.backgroundTheme]);

  const renderConditionsCard = () => {
    if (isError) {
      return null;
    }
    if (showConditionsCard) {
      return <TrailConditionsCard localizedDateString={localizedDateString} theme={overview?.backgroundTheme} trailId={trailId} />;
    }
    if (!initialTheme) {
      return <TrailConditionsUpsellCardLoading />;
    }
    return <TrailConditionsUpsellCard initialTheme={initialTheme} trailId={trailId} onShowUpsellModal={() => setShowConditionsUpsellModal(true)} />;
  };

  return (
    <>
      <div className={styles.container}>
        <Heading as="h4" size="400">
          <FormattedMessage defaultMessage="Weather" />
        </Heading>
        <div className={styles.content} ref={contentRef}>
          {renderDaySelectComponent()}
          {renderConditionsCard()}
        </div>
      </div>
      <ConditionsUpsellModal
        isOpen={showConditionsUpsellModal}
        onCloseRequest={(user?: User) => {
          setShowConditionsUpsellModal(false);

          if (user?.featureEntitlements?.includes('advanced_conditions')) {
            dispatch(updateIsConditionsPreviewActive(false));
          }
        }}
        upsellTrigger="advanced conditions layer tapped"
      />
    </>
  );
};

export default TrailConditions;
